import React, { useCallback, useEffect, useState } from 'react'
import './style.scss'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import Sticky from 'react-stickynode'
import Icon from '../../../../Shared/Icon'
import { ReactComponent as EyeIcon } from '../../../../../assets/img/icons/eye-slash.svg'
import { ReactComponent as CloseIcon } from '../../../../../assets/img/icons/close-small.svg'
import { ReactComponent as TrashIcon } from '../../../../Shared/Icon/img/trashcan.svg'
import TooltipWrapper from '../../../../Shared/TooltipWrapper'
import { GALLERIES_SHAPE } from '../../shapes'

const ActionBar = ({
                     selectedImageIds,
                     onCancelClick,
                     onDeleteRequest,
                     onHideFromPropertyGalleryRequest,
                     galleries
                   }) => {
  const { t } = useTranslation()
  const [onlyRoomImagesSelected, setOnlyRoomImagesSelected] = useState(false)

  // Effect for handling side-effects like event listeners
  useEffect(() => {
    const onKeydown = (e) => {
      if (e.keyCode === 27) {
        onCancelClick()
      }
    }

    document.addEventListener('keydown', onKeydown, false)

    /* Dispatch a resize event after render, so actionBar is sticky,
           even if user is already scrolled, otherwise actionbar will only be
           sticky/positioned after user scrolled.
           Issue: https://github.com/yahoo/react-stickynode/issues/61
           Yes 2 dispatches are needed
        */
    const resizeEvent = new Event('resize')
    window.dispatchEvent(resizeEvent)
    window.dispatchEvent(resizeEvent)

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('keydown', onKeydown, false)
    }
  }, [onCancelClick])

  useEffect(() => {
    const onlyRoomImagesSelected = selectedImageIds.every(id => {
      return Object.keys(galleries?.rooms || {}).some(roomId => {
        const room = galleries.rooms[roomId]
        return room.order?.some(roomImageId => roomImageId === id)
      })
    })
    setOnlyRoomImagesSelected(onlyRoomImagesSelected)
  }, [selectedImageIds, galleries])

  const handleHideClick = useCallback(() => {
    onHideFromPropertyGalleryRequest()
  }, [onHideFromPropertyGalleryRequest])

  const handleDeleteClick = useCallback(() => {
    onDeleteRequest(selectedImageIds)
  }, [onDeleteRequest, selectedImageIds])

  const selectedImagesCount = selectedImageIds.length

  const hideFromGalleryButton = <button
    disabled={!onlyRoomImagesSelected}
    className={`action-button ${onlyRoomImagesSelected ? '' : 'action-button--disabled'}`}
    onClick={handleHideClick}
  >
    <Icon icon={<EyeIcon/>} size="small"/>
    <span>{t('images.actionBar.actions.hideFromGallery')}</span>
  </button>
  const hideFromGalleryButtonWrapped = !onlyRoomImagesSelected ? <TooltipWrapper
      content={t('images.actionBar.tooltips.hideFromGallery')}
      place="top"
      maxWidth="320px"
      bgColor="#FEF4C9"
      positionStrategy="fixed"
    >
      {hideFromGalleryButton}
    </TooltipWrapper>
    : hideFromGalleryButton

  return (
    <Sticky className="image-browser-action-bar-wrapper" enabled={true} top={16}
            innerZ={1800} enableTransforms={false}>
      <div className={'image-browser-action-bar'}>
        <div className={'action-bar-info'}>
          <span>{t('N_SELECTED', { count: selectedImagesCount })}</span>
        </div>
        <div className={'action-bar-actions'}>
          {hideFromGalleryButtonWrapped}
          <button className="action-button" onClick={onCancelClick}>
            <Icon icon={<CloseIcon/>} size="extra-extra-small"/>
            <span>{t('CLEAR_SELECTION', 'Clear selection')}</span>
          </button>
          <button className="action-button action-button--delete" onClick={handleDeleteClick}>
            <Icon icon={<TrashIcon/>} size="extra-small"/>
            <span>{t('DELETE_SELECTED', 'Delete selected')}</span>
          </button>
        </div>
      </div>
    </Sticky>
  )
}

ActionBar.displayName = 'ActionBar'

ActionBar.propTypes = {
  selectedImageIds: PropTypes.array.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onDeleteRequest: PropTypes.func.isRequired,
  onHideFromPropertyGalleryRequest: PropTypes.func.isRequired,
  galleries: GALLERIES_SHAPE,
}

export default ActionBar
